import DOMPurify from 'dompurify';

export function comp(...arrayOfStrings: string[]): string {
	return arrayOfStrings.join(' ');
}

// This is faster that Lodash's cloneDeep and preferred as sometimes we need to copy the full Baobab tree
// https://stackoverflow.com/a/44612374
export function cloneDeep(o) {
	let newO;
	let i;

	if (typeof o !== 'object') {
		return o;
	}

	if (!o) {
		return o;
	}

	if (Object.prototype.toString.apply(o) === '[object Array]') {
		newO = [];
		for (i = 0; i < o.length; i += 1) {
			newO[i] = cloneDeep(o[i]);
		}
		return newO;
	}

	newO = {};
	for (i in o) {
		// eslint-disable-next-line no-prototype-builtins
		if (o.hasOwnProperty(i)) {
			newO[i] = cloneDeep(o[i]);
		}
	}
	return newO;
}

export const getNameFromPointSpreadValue = (name: string): string => {
	if (typeof name !== 'string') {
		return '';
	}

	return name.replace(/[+-]?(?:\d*\.)?\d+$/, '').trim();
};

export const getPointSpreadValueFromName = (name: string): string | number => {
	if (typeof name !== 'string') {
		return '';
	}

	let value = name;

	// 1. handle no numbers found, by returning an empty string
	const numbers = value.match(/\d+/g);
	if (!numbers) {
		return '';
	}

	// 2. remove undesired chars from our string, i.e. anything
	// that is not a digit(\d), plus(+), minus(-), or dot(.)
	const invalidCharsRegex = new RegExp(/[^\d+-.]/, 'g');
	if (value.match(invalidCharsRegex)) {
		value = value.replace(invalidCharsRegex, '');
	}

	// 3. locate desired substring index
	const lastIndexOfPlusChar = value.lastIndexOf('+');
	const lastIndexOfMinusChar = value.lastIndexOf('-');

	const hasPlusChar = lastIndexOfPlusChar !== -1;
	const hasMinusChar = lastIndexOfMinusChar !== -1;

	switch (true) {
		case hasPlusChar && hasMinusChar: {
			const [_, lastIndexOfPlusOrMinusChar] = [
				lastIndexOfPlusChar,
				lastIndexOfMinusChar,
			].sort();

			return value.substring(lastIndexOfPlusOrMinusChar);
		}
		case hasPlusChar && !hasMinusChar: {
			return value.substring(lastIndexOfPlusChar);
		}
		case !hasPlusChar && hasMinusChar: {
			return value.substring(lastIndexOfMinusChar);
		}
		case !hasPlusChar && !hasMinusChar: {
			return value;
		}
	}
};

export const isStrictlyUSHandicap = (bet): boolean => {
	if (!bet) {
		return false;
	}
	return (
		bet.marketName === 'Point Spread' ||
		bet.marketName === 'Moneyline' ||
		bet.marketName === 'Runline'
	);
};

export const getSearchParams = (
	url: string,
): [string, string][] | undefined => {
	if (!url) return;

	try {
		const searchString: string = getSearchString(url);
		const searchParams: URLSearchParams = new URLSearchParams(searchString);

		return [...searchParams.entries()];
	} catch (err) {
		console.log('error at hasSearchParams');
		console.error(err);
	}
};

export const getSearchString = (url: string = ''): string => {
	return url.split('?')[1];
};

export const getSearchParamsAndHashFragment = url => {
	const [currentPath, queryParams = '']: string[] = url.split('?');
	const [searchString = '', hashFragment = '']: string[] =
		queryParams.split('#');

	const searchParams: URLSearchParams = new URLSearchParams(searchString);

	const hasSearchParams: boolean = !![...searchParams.entries()].length;
	const hasHashFragment: boolean = !!hashFragment;

	const searchParamsString: string = hasSearchParams
		? `?${searchParams.toString()}`
		: '';
	const hashFragmentString: string = hasHashFragment ? `#${hashFragment}` : '';

	return {
		currentPath,
		hasSearchParams,
		searchParams,
		searchParamsString,
		hasHashFragment,
		hashFragment,
		hashFragmentString,
	};
};

export const getEnvironment = (url = '') => ({
	// context
	isClient: typeof window !== 'undefined',
	isServer: typeof window === 'undefined',

	// host
	isLocal: url.includes('localhost'),
	isTest: url.includes('frontend'),
	isStg: url.includes('stg.occloud.io'),
	isEphemeral: url.includes('stg.us.occloud.io'),
	isGreen: url.includes('green.prod.us.occloud.io'),
	isProd: url.includes('oddschecker.com'),
});

export function DomPurifySanitize(data: string) {
	if (typeof window !== 'undefined') {
		return DOMPurify.sanitize(data);
	}
	return data;
}

export function formatBookmakerStyles(
	brandColour: string,
	textColour: string | null,
): { backgroundColor: string; color: string } {
	return {
		backgroundColor: brandColour || '#fff',
		color: textColour === 'light' ? '#fff' : '#000',
	};
}
